<div class="merchant-wallet-container" *appHasPermission="{ resource: Resources.MerchantWallets, scope: Scopes.Read }">
  @if (error) {
    <div class="error-message">
      <span class="error-icon">⚠️</span>
      <span>{{ error }}</span>
      <button class="close-btn" (click)="error = null">×</button>
    </div>
  }

  @if (isBusy || isCreatingWallet || metadata.isLoading()) {
    <div class="loading-overlay" [class.half-page]="isCreatingWallet">
      <app-loading-spinner size="large"></app-loading-spinner>
    </div>
  }

  @if (isCreateWalletFormVisible) {
    <app-create-wallet-form
      [submitting]="isCreatingWallet"
      (cancelled)="closeCreateWalletForm()"
      (formSubmit)="createWallet($event)"
      class="full-page-form"
      />
  }

  @if (!isCreateWalletFormVisible) {
    <div>
      <div class="header-section">
        <div class="title-and-button">
          <h2 class="inner-tab-title">Wallets ({{ totalWallets }})</h2>
          <app-button
            buttonVariant="accent-add"
            buttonType="button"
            (buttonClick)="openCreateWalletForm()"
            *appHasPermission="{ resource: Resources.MerchantWallets, scope: Scopes.Create }"
            >
            <mat-icon>add</mat-icon>
            <span class="text">Create Wallet</span>
          </app-button>
        </div>
        <div class="actions">
          <app-button
            buttonVariant="filter"
            buttonType="button"
            [active]="filtersApplied"
            (buttonClick)="toggleFilters()"
            >
            <img [src]="'shared-assets/icons/filter.png'" alt="Filter Icon" class="filter-icon" />
            <span>Filters</span>
            @if (filtersApplied) {
              <span
                class="clear-icon"
                (click)="clearFilters(); $event.stopPropagation()"
                title="Clear filters"
                >
                <img src="shared-assets/icons/close.png" alt="Filter Icon" class="clear-icon" />
              </span>
            }
          </app-button>
          <app-pill-search
            [searchText]="searchText"
            placeholder="Search"
            (searchChange)="onSearchChange($event)"
            [isDisabled]="isDisabled"
          ></app-pill-search>
        </div>
      </div>
      <div class="wallet-tabs">
        <button
          type="button"
          class="wallet-tab"
          [ngClass]="{ active: activeTab === 'pooled' }"
          (click)="selectTab('pooled')"
          >
          <mat-icon class="wallet-tab-icon">layers</mat-icon>
          <span>Pooled ({{ poolWalletsCount }})</span>
        </button>
        <button
          type="button"
          class="wallet-tab"
          [ngClass]="{ active: activeTab === 'dedicated' }"
          (click)="selectTab('dedicated')"
          >
          <mat-icon class="wallet-tab-icon">account_balance_wallet</mat-icon>
          <span>Dedicated ({{ dedicatedWalletsCount }})</span>
        </button>
        <button
          type="button"
          class="wallet-tab"
          [ngClass]="{ active: activeTab === 'one-time' }"
          (click)="selectTab('one-time')"
          >
          <mat-icon class="wallet-tab-icon">bolt</mat-icon>
          <span>One-Time ({{ oneTimeWalletsCount }})</span>
        </button>
        <button type="button" class="wallet-tab" [ngClass]="{ active: activeTab === 'api' }" (click)="selectTab('api')">
          <mat-icon class="wallet-tab-icon">api</mat-icon>
          <span>API Wallets ({{ apiWalletsCount }})</span>
        </button>
      </div>
      @if (activeTab === 'pooled') {
        <div class="pooled-groups">
          @for (group of pooledGroups; track group.code) {
            <div class="risk-group">
              <div class="risk-group-header" (click)="toggleGroup(group.code)">
                <mat-icon class="group-chevron" [class.collapsed]="isGroupCollapsed(group.code)">expand_less</mat-icon>
                @if (group.code !== LEGACY_GROUP_CODE) {
                  <span
                    class="risk-dot"
                    [ngClass]="getGroupDotClass(group)"
                  ></span>
                }
                <span class="group-label">{{ group.label }}</span>
                <span class="group-count"
                  >({{ group.wallets.length }} wallet{{ group.wallets.length === 1 ? '' : 's' }})</span
                  >
                </div>
                @if (!isGroupCollapsed(group.code)) {
                  <div class="wallet-grid">
                    @for (row of walletsByNetwork(group.wallets); track row[0].networkType) {
                      <app-scroll-row>
                        @for (wallet of row; track wallet.id) {
                          <app-wallet-card
                            [id]="wallet.id"
                            [name]="wallet.name"
                            [address]="wallet.address"
                            [currencies]="getWalletCurrencies(wallet)"
                            [risk]="wallet.risk?.toLowerCase()"
                            [networkType]="wallet.networkType"
                            [pending]="wallet.pending"
                            [isActive]="wallet.isActive"
                            [lifecycleState]="wallet.lifecycleState"
                            [isServing]="wallet.isServing"
                            role="button"
                            tabindex="0"
                            (click)="openWalletDetail(wallet, 'Pool Wallet')"
                            (keydown.enter)="openWalletDetail(wallet, 'Pool Wallet')"
                            (keydown.space)="$event.preventDefault(); openWalletDetail(wallet, 'Pool Wallet')"
                            />
                        }
                      </app-scroll-row>
                    }
                  </div>
                }
              </div>
            }
            @if (pooledGroups.length === 0 && !isBusy) {
              <div class="empty-state">No pool wallets found.</div>
            }
          </div>
        }
        @if (activeTab === 'dedicated') {
          <div class="wallet-section dedicated-wallets">
            @if (dedicatedWallets.length > 0) {
              <div class="wallet-grid">
                @for (row of walletsByNetwork(dedicatedWallets); track row[0].networkType) {
                  <app-scroll-row>
                    @for (wallet of row; track wallet.id) {
                      <app-wallet-card
                        [id]="wallet.id"
                        [name]="wallet.name"
                        [address]="wallet.address"
                        [currencies]="getWalletCurrencies(wallet)"
                        [risk]="wallet.risk?.toLowerCase()"
                        [networkType]="wallet.networkType"
                        [pending]="wallet.pending"
                        [isActive]="wallet.isActive"
                        role="button"
                        tabindex="0"
                        (click)="openWalletDetail(wallet, 'Dedicated Wallet')"
                        (keydown.enter)="openWalletDetail(wallet, 'Dedicated Wallet')"
                        (keydown.space)="$event.preventDefault(); openWalletDetail(wallet, 'Dedicated Wallet')"
                        />
                    }
                  </app-scroll-row>
                }
              </div>
            }
            @if (dedicatedWallets.length === 0 && !isBusy) {
              <div class="empty-state">No dedicated wallets found.</div>
            }
          </div>
        }
        @if (activeTab === 'one-time') {
          <div class="wallet-section one-time-wallets">
            <div class="table-container">
              <table>
                <thead>
                  <tr>
                    <th>Wallet Address</th>
                    <th>Blockchain</th>
                    <th>Wallet Label</th>
                    <th>Last Interaction Date</th>
                    <th>Balance</th>
                    <th>Token Balance</th>
                    <th class="risk-level-header">Risk Level</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  @for (wallet of oneTimeWallets; track wallet.id) {
                    <tr
                      class="clickable"
                      tabindex="0"
                      (click)="openWalletDetail(wallet, 'One-time Wallet')"
                      (keydown.enter)="openWalletDetail(wallet, 'One-time Wallet')"
                      (keydown.space)="$event.preventDefault(); openWalletDetail(wallet, 'One-time Wallet')"
                      >
                      <td class="olv-address" [matTooltip]="wallet.address" matTooltipPosition="above">
                        {{ shortenAddress(wallet.address) }}
                      </td>
                      <td>{{ networkName(wallet.networkType) }}</td>
                      <td>{{ wallet.name }}</td>
                      <td>{{ wallet.interactionDate }}</td>
                      <td>{{ getNativeBalance(wallet) }}</td>
                      <td>{{ getTokenBalance(wallet) }}</td>
                      <td>
                        <div class="risk-container">
                          <app-state-badge type="risk" [value]="wallet.riskLevel"></app-state-badge>
                        </div>
                      </td>
                      <td>{{ wallet.status }}</td>
                    </tr>
                  }
                  @if (oneTimeWallets.length === 0 && !isBusy) {
                    <tr>
                      <td colspan="8">
                        <div class="empty-state">No One-time Use Wallets Yet.</div>
                      </td>
                    </tr>
                  }
                </tbody>
              </table>
              <app-show-more [isShowMore]="hasMoreWallets" [isLoading]="isMoreLoading" (loadMore)="loadMoreWallets()" />
            </div>
          </div>
        }
        @if (activeTab === 'api') {
          <div class="wallet-section one-time-wallets">
            <div class="table-container">
              <table>
                <thead>
                  <tr>
                    <th>Wallet Address</th>
                    <th>Blockchain</th>
                    <th>Type</th>
                    <th>Wallet Label</th>
                    <th>Last Interaction Date</th>
                    <th>Balance</th>
                    <th>Token Balance</th>
                    <th class="risk-level-header">Risk Level</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  @for (wallet of apiWallets; track wallet.id) {
                    <tr
                      class="clickable"
                      tabindex="0"
                (click)="
                  openWalletDetail(
                    wallet,
                    wallet.walletTypeLabel === 'Dedicated' ? 'Dedicated Wallet' : 'One-time Wallet'
                  )
                "
                (keydown.enter)="
                  openWalletDetail(
                    wallet,
                    wallet.walletTypeLabel === 'Dedicated' ? 'Dedicated Wallet' : 'One-time Wallet'
                  )
                "
                (keydown.space)="
                  $event.preventDefault();
                  openWalletDetail(
                    wallet,
                    wallet.walletTypeLabel === 'Dedicated' ? 'Dedicated Wallet' : 'One-time Wallet'
                  )
                "
                      >
                      <td class="olv-address" [matTooltip]="wallet.address" matTooltipPosition="above">
                        {{ shortenAddress(wallet.address) }}
                      </td>
                      <td>{{ networkName(wallet.networkType) }}</td>
                      <td>{{ wallet.walletTypeLabel }}</td>
                      <td>{{ wallet.name }}</td>
                      <td>{{ wallet.interactionDate }}</td>
                      <td>{{ getNativeBalance(wallet) }}</td>
                      <td>{{ getTokenBalance(wallet) }}</td>
                      <td>
                        <div class="risk-container">
                          <app-state-badge type="risk" [value]="wallet.riskLevel"></app-state-badge>
                        </div>
                      </td>
                      <td>{{ wallet.status }}</td>
                    </tr>
                  }
                  @if (apiError && !isApiLoading) {
                    <tr>
                      <td colspan="9">
                        <div class="empty-state">{{ apiError }}</div>
                      </td>
                    </tr>
                  }
                  @if (apiWallets.length === 0 && !isBusy && !isApiLoading && !apiError) {
                    <tr>
                      <td colspan="9">
                        <div class="empty-state">No API Wallets Yet.</div>
                      </td>
                    </tr>
                  }
                </tbody>
              </table>
              <app-show-more
                [isShowMore]="hasMoreApiWallets"
                [isLoading]="isMoreApiLoading"
                (loadMore)="loadMoreApiWallets()"
                />
            </div>
          </div>
        }
      </div>
    }
  </div>
